import { DiscoveryApi, FetchApi } from "@backstage/core-plugin-api";
import { UmamiAnalyticsApi } from "./UmamiAnalyticsApi";
import { WebsiteStats, WebsitePageviews, WebsiteMetrics, WebsiteMetricsEventsSeries } from "../types";

export class UmamiAnalyticsApiImpl implements UmamiAnalyticsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getWebsiteStats(params: {
    websiteId: string;
    startDate: string;
    endDate: string;
  }): Promise<WebsiteStats> {

    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('umami-analytics');
      const url = `${baseUrl}/websites/${params.websiteId}/stats?startDate=${params.startDate}&endDate=${params.endDate}`;

      const response = await this.fetchApi.fetch(url);

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`HTTP ${response.status}: Failed to fetch website stats data ${errorText}`);
      }

      const data: WebsiteStats = await response.json();
      return data;

    } catch (error) {
      throw error;
    }
  }

  async getWebsitePageviews(params: {
      websiteId: string;
      startDate: string;
      endDate: string;
      unit?: 'hour' | 'day' | 'month';
      timezone?: string;
    }): Promise<WebsitePageviews> {

      try {
        const baseUrl = await this.discoveryApi.getBaseUrl('umami-analytics');

        const queryParams = new URLSearchParams({
          startDate: params.startDate,
          endDate: params.endDate,
          unit: params.unit || 'hour',
          timezone: params.timezone || 'UTC',
        });

        const url = `${baseUrl}/websites/${params.websiteId}/pageviews?${queryParams}`;
        const response = await this.fetchApi.fetch(url);

        if (!response.ok) {
          const errorText = await response.text();

          throw new Error(`HTTP ${response.status}: Failed to fetch website pageviews data ${errorText}`);
        }

        const data: WebsitePageviews = await response.json();
        return data;

      } catch (error) {
        throw error;
      }
    }

  async getWebsiteMetrics(params: { websiteId: string; startDate: string; endDate: string; type: "url" | "referrer" | "browser" | "os" | "device" | "country" | "event"; limit: number; }): Promise<WebsiteMetrics> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('umami-analytics');

      const queryParams = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
        type: params.type,
        ...(params.limit? {limit: String(params.limit)} : {})
      });

      const url = `${baseUrl}/websites/${params.websiteId}/metrics?${queryParams}`;
      const response = await this.fetchApi.fetch(url);

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`HTTP ${response.status}: Failed to fetch website metrics data ${errorText}`);
      }
      const data: WebsiteMetrics = await response.json();
      return data;
    } catch (error) {
      throw error
    }
  }

  async getWebsiteEventSeries(params: { websiteId: string; startDate: string; endDate: string; unit?: "hour" | "day" | "month" | "year"; timezone?: string; }): Promise<WebsiteMetricsEventsSeries> {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('umami-analytics');

      const queryParams = new URLSearchParams({
          startDate: params.startDate,
          endDate: params.endDate,
          unit: params.unit || 'hour',
          timezone: params.timezone || 'UTC',
      })

      const url = `${baseUrl}/websites/${params.websiteId}/events/series?${queryParams}`;
      const response = await this.fetchApi.fetch(url);

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`HTTP ${response.status}: Failed to fetch website event series data ${errorText}`);
      }

      const data: WebsiteMetricsEventsSeries = await response.json();
      return data;
    } catch (error) {
        throw error;
      }
  }
}
